// Media Upload Service - integracja z Cloudinary lub lokalnym storage
import { supabase } from '@/config/supabase';

interface UploadResult {
  url: string;
  publicId: string;
  format: string;
  width?: number;
  height?: number;
  size: number;
  thumbnail?: string;
}

export class MediaUploadService {
  private cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'plexisystem';
  private uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'social_media';
  
  // Upload do Cloudinary (bez backendu - bezpośrednio z frontu)
  async uploadToCloudinary(file: File): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);
    formData.append('folder', 'social-media');
    
    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.cloudName}/auto/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error('Cloudinary upload failed');
      }
      
      const data = await response.json();
      
      return {
        url: data.secure_url,
        publicId: data.public_id,
        format: data.format,
        width: data.width,
        height: data.height,
        size: data.bytes,
        thumbnail: data.eager?.[0]?.secure_url || data.secure_url
      };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      // Fallback do Supabase Storage
      return this.uploadToSupabase(file);
    }
  }
  
  // Fallback - Upload do Supabase Storage
  async uploadToSupabase(file: File): Promise<UploadResult> {
    const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = `social-media/${fileName}`;
    
    // Upload do Supabase Storage
    const { data, error } = await supabase.storage
      .from('media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      throw new Error(`Upload failed: ${error.message}`);
    }
    
    // Pobierz publiczny URL
    const { data: { publicUrl } } = supabase.storage
      .from('media')
      .getPublicUrl(filePath);
    
    // Dla obrazów, stwórz thumbnail URL
    let thumbnail = publicUrl;
    if (file.type.startsWith('image/')) {
      // Supabase obsługuje transformacje obrazów
      thumbnail = `${publicUrl}?width=300&height=300`;
    }
    
    return {
      url: publicUrl,
      publicId: fileName,
      format: file.type.split('/')[1] || 'unknown',
      size: file.size,
      thumbnail
    };
  }
  
  // Upload wielu plików
  async uploadMultiple(files: File[]): Promise<UploadResult[]> {
    const uploads = files.map(file => this.upload(file));
    return Promise.all(uploads);
  }
  
  // Główna metoda upload
  async upload(file: File): Promise<UploadResult> {
    // Walidacja
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 10MB');
    }
    
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Allowed: JPG, PNG, GIF, WEBP, MP4');
    }
    
    // Spróbuj Cloudinary, jeśli się nie uda to Supabase
    try {
      if (this.cloudName && this.cloudName !== 'plexisystem') {
        return await this.uploadToCloudinary(file);
      }
    } catch (error) {
      console.warn('Cloudinary not configured, using Supabase Storage');
    }
    
    return await this.uploadToSupabase(file);
  }
  
  // Optymalizacja obrazu przed uploadem
  async optimizeImage(file: File, maxWidth: number = 1200): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            resolve(file);
            return;
          }
          
          // Oblicz nowe wymiary
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (maxWidth / width) * height;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Rysuj i kompresuj
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const optimizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(optimizedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.85 // Jakość 85%
          );
        };
        
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  }
  
  // Usuń media
  async delete(publicId: string): Promise<boolean> {
    try {
      // Jeśli to Supabase Storage
      if (!publicId.includes('cloudinary')) {
        const { error } = await supabase.storage
          .from('media')
          .remove([`social-media/${publicId}`]);
        
        return !error;
      }
      
      // Dla Cloudinary wymagałoby to backendu (signature)
      console.warn('Cloudinary deletion requires backend implementation');
      return false;
    } catch (error) {
      console.error('Delete media error:', error);
      return false;
    }
  }
}

// Export singleton
export const mediaUploadService = new MediaUploadService();
